import * as React from "react";
import { FormEvent, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import styled, { css } from "styled-components";
import { Box } from "src/components/Box";
import { Button } from "src/components/Button";
import Checkbox from "@asphalt-react/checkbox";
import { List } from "src/components/List";
import { IDType, MediaQueries, TNC_VERSION } from "src/constants";
import { PASSPORT_FORM_ROUTES, PASSPORT_ROUTES } from "src/constants/routes";
import { PassportTnCModal } from "./TermsAndConditions.modal";
import { Text } from "src/components/Text";
import moment from "moment";
import { observer } from "mobx-react-lite";
import { usePassportStore } from "src/stores/Passport.store";
import { useTermsAndConditionsState } from "./TermsAndConditions.state";
import { usePassportImprovements, useTranslation } from "src/hooks";
import { CookieNames, deleteCookie, setCookie } from "src/helpers/cookies";
import { resolveStepFieldError } from "../stepFieldError";
import { errFormatter } from "src/helpers/errorFormatter";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { useErrorModalStore } from "src/components/ErrorModal/ErrorModal.store";
import { LoadingOverlay } from "../../../components";

const getMidtransTncVersion = () => {
  const { VERSION, REVISION } = TNC_VERSION.MIDTRANS;
  return `${VERSION}.${REVISION}`;
};

const ButtonWrapperEl = styled(Box)`
  > button {
    min-width: 144px;
  }

  ${MediaQueries.screen.medium.minWidth} {
    ${(p) => css`
      border-top: 2px solid ${p.theme.colors.borders.light};
      padding-top: ${p.theme.dimension.spacing.xl}px;
    `}
  }
`;

export const PassportTnCForm = observer(() => {
  const HASH = "#tnc-modal";
  const translate = useTranslation();
  const history = useHistory();
  const store = usePassportStore();
  const state = useTermsAndConditionsState();
  const { agreeToTnC, toggleAgreeToTnC } = state;
  const errorModalStore = useErrorModalStore();
  const {
    isImprovementEnabled,
    enableImprovementsAndRedirect,
  } = usePassportImprovements();

  const isCardPayment = store.outlet.metadata.midtrans_credit_card_optin;

  const isWni = store?.outlet?.json?.id_type === IDType.KTP;

  useEffect(() => {
    eventDispatcher.publish({ action: "ob_tnc_opened" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doOnBoarding = async (event: FormEvent) => {
    event?.preventDefault();
    state.agreementDate = moment();
    state.onboardAt = moment();

    try {
      if (!isImprovementEnabled) {
        await enableImprovementsAndRedirect();
        return;
      } else {
        await store.doPassportKYB(isWni);
      }
      await Promise.all([
        store.recordConsent(getMidtransTncVersion()),
        store.updateOutlet(state.toJSON(), true),
      ]);
      deleteCookie(CookieNames.PassportStep);
      eventDispatcher.publish({ action: "ob_tnc_next" });
      history.push(PASSPORT_ROUTES.onboardingCompleted);
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_tnc_error",
        payload: {
          message: message,
          httpStatus: status,
        },
      });

      const route = resolveStepFieldError(error);
      if (route) {
        const step = PASSPORT_FORM_ROUTES.find(({ path }) => path === route);
        errorModalStore.showAlert({
          title: translate("passport_generic.field_error.title"),
          message: translate("passport_generic.field_error.message", [
            translate(`stepper.${step?.label}`),
          ]),
          cta: translate("passport_generic.field_error.cta"),
        });
        errorModalStore.retryCta = () => {
          setCookie(CookieNames.PassportStep, route);
          history.push(route);
        };
        return;
      }

      errorModalStore.showAlert();
      errorModalStore.retryCta = doOnBoarding;
    }
  };

  return (
    <Box as="form" onSubmit={doOnBoarding}>
      <Text as="p" marginBottomM>
        {translate("terms_and_conditions.new_brief.0")}
        <Text fontWeight="demi" as="span">
          {translate("terms_and_conditions.new_brief.1")}
        </Text>
        {translate("terms_and_conditions.new_brief.2")}
      </Text>
      <List>
        <Text as="li" fontWeight="book">
          {translate("terms_and_conditions.new_content.0")}
        </Text>
        <Text as="li" fontWeight="book">
          {translate("terms_and_conditions.new_content.1")}
        </Text>
        <Text as="li" fontWeight="book">
          {translate("terms_and_conditions.new_content.2")}
        </Text>
        {isCardPayment && (
          <Text as="li" fontWeight="book">
            {translate("terms_and_conditions.new_content.3")}
          </Text>
        )}
      </List>
      <Box
        marginTopXl
        paddingVerticalM
        style={{ borderTop: "1px solid rgb(230, 233, 239)" }}
      >
        <Text as="p" marginBottomS>
          {translate("terms_and_conditions.new_consent.0.0")}
          <a
            href="https://midtrans.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Text fontWeight="demi" as="span" color="primary">
              {translate("terms_and_conditions.new_consent.0.1")}
            </Text>
          </a>
          {translate("terms_and_conditions.new_consent.0.2")}
        </Text>
        <Text as="p" marginBottomS>
          {translate("terms_and_conditions.new_consent.1.0")}
          <Text as="span" fontWeight="demi">
            {translate("terms_and_conditions.new_consent.1.1")}
          </Text>
          {translate("terms_and_conditions.new_consent.1.2")}
          <Text as="span" fontWeight="demi">
            {translate("terms_and_conditions.new_consent.1.3")}
          </Text>
          {translate("terms_and_conditions.new_consent.1.4")}
        </Text>
        <Checkbox
          label={
            <Text>
              {translate("terms_and_conditions.checkbox_label.0")}
              <a href={window.location.pathname + HASH} className="asp-link">
                <Text fontWeight="demi" as="span" color="primary">
                  {translate("terms_and_conditions.checkbox_label.1")}
                </Text>
              </a>
            </Text>
          }
          id="passport-accept-agreement"
          size="s"
          checked={agreeToTnC}
          onChange={toggleAgreeToTnC}
        />
      </Box>

      <ButtonWrapperEl flex justifyBetween marginTopXl>
        <Link to={PASSPORT_ROUTES.bankDetails}>
          <Button
            outline
            onClick={() => eventDispatcher.publish({ action: "ob_tnc_back" })}
          >
            {translate("terms_and_conditions.cta.negative")}
          </Button>
        </Link>
        <Button
          type="submit"
          disabled={!agreeToTnC}
          loading={
            store?.loadingState?.has?.updateOutlet! ||
            store?.loadingState?.has?.recordConsent! ||
            store?.loadingState?.has?.doPassportKYB! ||
            store?.loadingState?.has?.addFeatureTypes!
          }
          onClick={() =>
            eventDispatcher.publish({
              action: "ob_bankinfo_validate",
              payload: {
                customer_id: store.authUser.merchantId,
                email: store.authUser.email,
                current_time: new Date().toISOString(),
              },
            })
          }
        >
          {translate("terms_and_conditions.cta.positive")}
        </Button>
      </ButtonWrapperEl>

      <PassportTnCModal hash={HASH} />
      <LoadingOverlay
        show={
          store?.loadingState?.has?.updateOutlet! ||
          store?.loadingState?.has?.recordConsent! ||
          store?.loadingState?.has?.doPassportKYB! ||
          store?.loadingState?.has?.addFeatureTypes!
        }
      />
    </Box>
  );
});
